import {compact} from '@benchling/js-utils';
import {produce, Draft} from 'immer';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import areEqual from 'react-fast-compare';
import {
  applyEdgeChanges,
  applyNodeChanges,
  Connection,
  Edge,
  EdgeChange,
  Node,
  NodeChange,
  ReactFlowInstance,
} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {removeFunctionSpecsUsingEdge} from 'processes/graph-builder-processes/flowchart-viz/action-functions/deleteEdgeProcessFlowchartActionFunction';
import {LockedContext} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/LockedContext';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {
  CreateOrUpdateRouterFunctionSpec,
  RouterConfig,
  UNCONFIGURED_DEFAULT_ROUTER_FUNCTION_SPEC,
  UNCONFIGURED_NON_DEFAULT_ROUTER_FUNCTION_SPEC,
} from 'processes/modals/router-config/RouterConfigConstants';
import {isNewEdgeValidForFlowchart} from 'processes/modals/utils/TransitionModalValidations';
import {omit} from 'util/omitKey';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {AddNodeOption, getAddNodeOptions} from './AddNodeDropdown';
import buildInteractiveFlowchartFromStaticData from './buildInteractiveFlowchartFromStaticData';
import {buildControlFlowEdge} from './buildStaticDataFromFlowchartConfigGraphQL';
import {isNewEdgeDuplicate} from './edgeUtils';
import {getEdgeTypeFromHandleId} from './flowchartIdentifierUtils';
import {
  FlowchartEdge,
  FlowchartStaticEdgeData,
  INTERACTION_FIELDS,
  ProcessFlowchartEdgeType,
  ProcessFlowchartNodeType,
  StaticTemplateFlowchart,
} from './processFlowchartConfigEditorConstants';
import {
  FlowchartNode,
  isInteractivePlaceholderNode,
  isInteractiveRouterNode,
  RouterStaticNodeData,
  StaticFlowchartNodeData,
} from './processFlowchartConfigEditorNodeTypes';
import {
  FlowchartCustomMappingDataChange,
  FlowchartCustomMappingDataChangeType,
  FlowchartEdgeChange,
  FlowchartEdgeChangeType,
  FlowchartNodeChange,
  FlowchartNodeChangeType,
  FlowchartOptions,
  FlowchartTransitionSourceChange,
  FlowchartTransitionSourceChangeType,
} from './processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from './redux/flowchartConfigAutosaveManagerCallbacks';
import {FlowchartConfigWithPartialMetadata} from './redux/FlowchartConfigStateManager';
import {FlowchartConfigMetadata} from './redux/reduceFlowchartConfig';
import {SingletonTaskSchemasByName} from './SingletonTaskSchemasContext';
import {ConfigurableTaskDefinitionGQL} from './useAllSchemasById';

// Callers will generate the changes to be applied to a given draft flowchart state,
// which applyFlowchartChanges will then apply to the draft state.
export type FlowchartChangeGenerator = (flowchartConfig: FlowchartConfigWithMetadata) => {
  nodeChanges?: Array<FlowchartNodeChange>;
  edgeChanges?: Array<FlowchartEdgeChange>;
  transitionSourceChanges?: Array<FlowchartTransitionSourceChange>;
  customMappingChanges?: Array<FlowchartCustomMappingDataChange>;
};

interface UseFlowchartStateReturn {
  // Interactive state
  interactiveNodes: Array<FlowchartNode>;
  interactiveEdges: Array<FlowchartEdge>;
  // Core functions
  applyFlowchartChanges: (changeGenerator: FlowchartChangeGenerator) => void;
  onReactFlowNodeChangesOnly: (nodeChanges: Array<FlowchartNodeChange>) => void;
  onReactFlowEdgeChangesOnly: (edgeChanges: Array<FlowchartEdgeChange>) => void;
  onConnect: (connection: Connection) => void;
  setNodesViaChangesForLayout: (newNodes: Array<Node>) => void;
  setEdgesViaChangesForLayout: (newEdges: Array<Edge>) => void;
  // Sidepanel state
  isRightSidepanelOpen: boolean;
  selectedFieldId: string | null;
  selectedNodeIdForRightSidepanel: string | null;
  setSelectedFieldId: (fieldId: string | null) => void;
  // Sidepanel functions
  handleRightSidepanelClose: () => void;
  openSidepanelForNode: (nodeId: string) => void;
  openSidepanelForNodeWithField: (nodeId: string, fieldId: string) => void;
  getAddNodeOptionsForRightHandle: (
    sourceNodeId: string,
    reactFlowInstance: ReactFlowInstance
  ) => Array<AddNodeOption> | null;
  // Node addition state
  sourceNodeAndNewNodeBeingAdded: [string, FlowchartNode] | null;
  setSourceNodeAndNewNodeBeingAdded: (value: [string, FlowchartNode] | null) => void;
  placeholderNodeIdBeingAdded: string | null;
  setPlaceholderNodeBeingAdded: (value: string | null) => void;
}

export interface FlowchartChangeResult {
  updatedNodes: Array<FlowchartNode>;
  updatedEdges: Array<FlowchartEdge>;

  /**
   * Whether or not the static data of any nodes has changed.
   * This includes whether or not any nodes were added or removed.
   */
  hasNodeDataChanges: boolean;

  /**
   * Whether or not the structure of the flowchart has changed.
   * This includes whether or not any edges were added or removed.
   */
  hasStructuralChanges: boolean;
}

export interface UseFlowchartStateParams {
  staticFlowchart: StaticTemplateFlowchart;
  flowchartConfigMetadata: FlowchartConfigMetadata;
  validationErrors: Array<ProcessFlowchartValidationError>;
  setFlowchartAndMetadata: (
    updater: (currentState: FlowchartConfigWithMetadata) => FlowchartConfigWithPartialMetadata | null,
    saveImmediately?: boolean
  ) => void;
  // Extra parameters needed for adding a node via a right handle click
  addNodeParams: {
    flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
    rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
    taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
    didInitialFlowchartHaveChainedRouters: boolean;
    singletonTaskSchemasByName: SingletonTaskSchemasByName;
  };
}

/**
 * Validates edge changes and filters out invalid changes.
 */
function validateEdgeChanges(
  edgeChanges: Array<FlowchartEdgeChange>,
  existingEdges: Array<FlowchartStaticEdgeData>
): Array<FlowchartEdgeChange> {
  return edgeChanges.filter((edgeChange) => {
    // Only validate 'add' changes, others are always valid
    if (edgeChange.type !== FlowchartEdgeChangeType.ADD || !edgeChange.item) {
      return true;
    }

    return isNewEdgeValidForFlowchart(edgeChange.item, existingEdges);
  });
}

function cleanupOrphanedEdgesAfterNodeRemoval(
  draft: Draft<FlowchartConfigWithMetadata>,
  removedNodeId: string
): void {
  draft.staticFlowchart.edgesStaticData = draft.staticFlowchart.edgesStaticData.filter(
    (edge) => edge.source !== removedNodeId && edge.target !== removedNodeId
  );
}

/**
 * Extracts edges of a given type between nodes that contain the same schema ID from static data.
 */
function extractEdgesBetweenNodesWithMatchingDataFromStatic(
  edges: Array<FlowchartStaticEdgeData>,
  sourceNodeId: string,
  targetNodeId: string,
  nodes: Array<StaticFlowchartNodeData>,
  edgeType: ProcessFlowchartEdgeType
): Array<FlowchartStaticEdgeData> {
  const sourceNodeIds = findNodesWithMatchingPTDVFromStatic(nodes, sourceNodeId);
  const targetNodeIds = findNodesWithMatchingPTDVFromStatic(nodes, targetNodeId);

  return edges.filter(
    (edge) =>
      sourceNodeIds.includes(edge.source) && targetNodeIds.includes(edge.target) && edge.type === edgeType
  );
}

/**
 * Processes edge changes with mirroring logic and returns all changes including mirror changes.
 */
function processEdgeChangesWithMirroring(
  edgeChanges: Array<FlowchartEdgeChange>,
  draft: Draft<FlowchartConfigWithMetadata>
): Array<FlowchartEdgeChange> {
  const allEdgeChanges = [...edgeChanges];

  // Add mirroring changes for edge operations
  for (const edgeChange of edgeChanges) {
    if (edgeChange.type === 'remove') {
      // Calculate mirror changes for removed edges
      const deletedEdge = draft.staticFlowchart.edgesStaticData.find((edge) => edge.id === edgeChange.id);
      if (deletedEdge && deletedEdge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
        const edgesBetweenSameSchemas = extractEdgesBetweenNodesWithMatchingDataFromStatic(
          draft.staticFlowchart.edgesStaticData,
          deletedEdge.source,
          deletedEdge.target,
          draft.staticFlowchart.nodesStaticData,
          ProcessFlowchartEdgeType.DATA_FLOW
        );
        const edgesBetweenSameSchemasWithControlFlow = edgesBetweenSameSchemas.filter((edge) =>
          draft.staticFlowchart.edgesStaticData.some(
            (e) =>
              e.source === edge.source &&
              e.target === edge.target &&
              e.type === ProcessFlowchartEdgeType.CONTROL_FLOW
          )
        );

        if (edgesBetweenSameSchemasWithControlFlow.length > 0) {
          const mirrorEdgesToRemove = edgesBetweenSameSchemasWithControlFlow
            .filter(
              (edge) =>
                edge.sourceHandle === deletedEdge.sourceHandle &&
                edge.targetHandle === deletedEdge.targetHandle
            )
            .map((edge) => edge.id);

          allEdgeChanges.push(
            ...mirrorEdgesToRemove.map((edgeId) => ({type: 'remove' as const, id: edgeId}))
          );
        }
      }
    } else if (edgeChange.type === 'add') {
      // Calculate mirror changes for added edges
      const newEdge = edgeChange.item;
      if (newEdge && newEdge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
        const controlFlowEdgesToMirrorDataFlow = extractEdgesBetweenNodesWithMatchingDataFromStatic(
          draft.staticFlowchart.edgesStaticData,
          newEdge.source,
          newEdge.target,
          draft.staticFlowchart.nodesStaticData,
          ProcessFlowchartEdgeType.CONTROL_FLOW
        );

        if (controlFlowEdgesToMirrorDataFlow.length > 0) {
          const mirrorEdges = compact(
            controlFlowEdgesToMirrorDataFlow.map((controlEdge) => {
              const mirrorEdge: FlowchartEdge = {
                ...controlEdge,
                id: uuidv4(),
                sourceHandle: newEdge.sourceHandle!,
                targetHandle: newEdge.targetHandle!,
                type: ProcessFlowchartEdgeType.DATA_FLOW,
              };
              // Filter out duplicate edges
              if (isNewEdgeDuplicate(draft.staticFlowchart.edgesStaticData, mirrorEdge)) {
                return null;
              }
              return mirrorEdge;
            })
          );

          allEdgeChanges.push(...mirrorEdges.map((mirrorEdge) => ({type: 'add' as const, item: mirrorEdge})));
        }
      }
    }
  }

  return allEdgeChanges;
}

function getEdgesRouterFunctionsDifference(
  routerConfig: RouterConfig,
  numRouterNodeOutboundEdges: number
): number {
  const numExistingFunctionSpecs =
    routerConfig.functionSpecs.length + (routerConfig.defaultFunctionSpec != null ? 1 : 0);
  const numEdgesWithoutFunctionSpecs = numRouterNodeOutboundEdges - numExistingFunctionSpecs;

  return numEdgesWithoutFunctionSpecs;
}

function addUnconfiguredFunctionsForRouterConfig(
  routerConfig: RouterConfig,
  numEdgesWithoutFunctionSpecs: number,
  numRouterOutboundEdges: number
): RouterConfig {
  const newFunctionSpecs: Array<CreateOrUpdateRouterFunctionSpec> = [];
  const numNewFunctionSpecsNeeded =
    routerConfig.defaultFunctionSpec == null
      ? numEdgesWithoutFunctionSpecs - 1
      : numEdgesWithoutFunctionSpecs;

  let newFunctionSpecCreated = 0;
  let defaultFunctionSpec = routerConfig.defaultFunctionSpec;
  if (defaultFunctionSpec == null) {
    defaultFunctionSpec = {...UNCONFIGURED_DEFAULT_ROUTER_FUNCTION_SPEC, clientId: uuidv4()};
    newFunctionSpecCreated++;
  }
  while (newFunctionSpecCreated < numNewFunctionSpecsNeeded) {
    newFunctionSpecs.push({
      ...UNCONFIGURED_NON_DEFAULT_ROUTER_FUNCTION_SPEC,
      name: `Route ${numRouterOutboundEdges - numNewFunctionSpecsNeeded + newFunctionSpecCreated}`,
      clientId: uuidv4(),
    });
    newFunctionSpecCreated++;
  }
  return {
    ...routerConfig,
    functionSpecs: routerConfig.functionSpecs.concat(newFunctionSpecs),
    defaultFunctionSpec,
  };
}

function removeUnusedFunctionSpecs(
  routerConfig: RouterConfig,
  numEdgesWithoutFunctionSpecs: number
): RouterConfig {
  assert(numEdgesWithoutFunctionSpecs > 0, 'Expected numEdgesWithoutFunctionSpecs to be positive');
  const newFunctionSpecs = [];
  let functionSpecsRemoved = 0;
  for (const functionSpec of routerConfig.functionSpecs) {
    if (
      functionSpec.edgeConfigReference != null ||
      functionSpecsRemoved >= Math.abs(numEdgesWithoutFunctionSpecs)
    ) {
      newFunctionSpecs.push(functionSpec);
    } else {
      functionSpecsRemoved++;
    }
  }

  const numSpecsStillToRemove = numEdgesWithoutFunctionSpecs - functionSpecsRemoved;
  let defaultFunctionSpec = routerConfig.defaultFunctionSpec;
  if (numSpecsStillToRemove > 0 && defaultFunctionSpec?.edgeConfigReference == null) {
    defaultFunctionSpec = null;
    functionSpecsRemoved++;
  }
  assert(
    functionSpecsRemoved === numEdgesWithoutFunctionSpecs,
    'Did not remove expected number of function specs',
    {
      functionSpecsRemoved,
      numEdgesWithoutFunctionSpecs,
      defaultFunctionSpec,
    }
  );

  routerConfig = {
    ...routerConfig,
    functionSpecs: newFunctionSpecs,
    defaultFunctionSpec,
  };
  return routerConfig;
}

/**
 * Adding/removing an edge requires recalculating function specs for router nodes.
 */
function updateRouterConfigsAfterEdgeChange(
  draft: Draft<FlowchartConfigWithMetadata>,
  changeType: FlowchartEdgeChangeType.ADD | FlowchartEdgeChangeType.REMOVE,
  edgeId?: string
): void {
  // Only process if we have router nodes
  const routerNodes = draft.staticFlowchart.nodesStaticData.filter(
    (node) => node.nodeType === ProcessFlowchartNodeType.ROUTER_NODE
  ) as Array<RouterStaticNodeData>;

  if (routerNodes.length === 0) {
    return;
  }

  for (let i = 0; i < draft.staticFlowchart.nodesStaticData.length; i++) {
    const node = draft.staticFlowchart.nodesStaticData[i];

    if (node.nodeType !== ProcessFlowchartNodeType.ROUTER_NODE) {
      continue;
    }

    const routerNode = node as RouterStaticNodeData;
    let updatedRouterConfig: RouterConfig = {...routerNode.routerConfig};

    // Handle edge removal
    if (changeType === FlowchartEdgeChangeType.REMOVE && edgeId) {
      updatedRouterConfig = removeFunctionSpecsUsingEdge(edgeId, updatedRouterConfig);
    }

    // Recalculate function specs based on current edges
    const numOutboundEdges = draft.staticFlowchart.edgesStaticData.filter(
      (edge) => edge.source === node.id
    ).length;
    const numEdgesWithoutFunctionSpecs = getEdgesRouterFunctionsDifference(
      updatedRouterConfig,
      numOutboundEdges
    );

    if (numEdgesWithoutFunctionSpecs > 0) {
      updatedRouterConfig = addUnconfiguredFunctionsForRouterConfig(
        updatedRouterConfig,
        numEdgesWithoutFunctionSpecs,
        numOutboundEdges
      );
    } else if (numEdgesWithoutFunctionSpecs < 0) {
      updatedRouterConfig = removeUnusedFunctionSpecs(
        updatedRouterConfig,
        Math.abs(numEdgesWithoutFunctionSpecs)
      );
    }

    // Update the node in the draft
    (draft.staticFlowchart.nodesStaticData[i] as RouterStaticNodeData).routerConfig = updatedRouterConfig;
  }
}

/**
 * Finds nodes that have the same PTDV ID as the node with the given ID from static data.
 */
function findNodesWithMatchingPTDVFromStatic(
  nodes: Array<StaticFlowchartNodeData>,
  nodeId: string
): Array<string> {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) {
    return [];
  }
  if (node.nodeType === ProcessFlowchartNodeType.TASK_SCHEMA_NODE) {
    const taskDefinitionVersionId = node.taskDefinitionVersionSpec.clientId;
    const nodeDetailsType = node.nodeDetailsType;
    return nodes
      .filter(
        (n) =>
          n.nodeType === ProcessFlowchartNodeType.TASK_SCHEMA_NODE &&
          n.taskDefinitionVersionSpec.clientId === taskDefinitionVersionId &&
          n.nodeDetailsType === nodeDetailsType &&
          n.id !== nodeId // Exclude the node itself
      )
      .map((n) => n.id);
  }
  return [];
}

function applyFlowchartNodeChanges(
  nodeChanges: Array<FlowchartNodeChange>,
  draft: Draft<FlowchartConfigWithMetadata>,
  setNodeDimensions: React.Dispatch<React.SetStateAction<Record<string, {width: number; height: number}>>>
): void {
  nodeChanges.forEach((change) => {
    switch (change.type) {
      case FlowchartNodeChangeType.ADD:
        if (change.item != null) {
          const newNodeData: StaticFlowchartNodeData = extractStaticData(change.item.data);
          draft.staticFlowchart.nodesStaticData.push(newNodeData);
          draft.metadata.interactionState.nodePositionsByNodeId[change.item.id] = change.item.position || {
            x: 0,
            y: 0,
          };
        }
        break;
      case FlowchartNodeChangeType.POSITION:
        if (change.position) {
          draft.metadata.interactionState.nodePositionsByNodeId[change.id] = change.position;
        }
        break;
      case FlowchartNodeChangeType.SELECT: {
        // Only modify the Set if the change would actually change it (prevents unnecessary re-renders)
        const isCurrentlySelected = draft.metadata.interactionState.selectedNodeIds.has(change.id);
        if (change.selected && !isCurrentlySelected) {
          draft.metadata.interactionState.selectedNodeIds.add(change.id);
        } else if (!change.selected && isCurrentlySelected) {
          draft.metadata.interactionState.selectedNodeIds.delete(change.id);
        }
        break;
      }
      case FlowchartNodeChangeType.REMOVE:
        assert(
          draft.staticFlowchart.nodesStaticData.some((node) => node.id === change.id),
          'Node does not exist in static data'
        );
        draft.staticFlowchart.nodesStaticData = draft.staticFlowchart.nodesStaticData.filter(
          (node) => node.id !== change.id
        );
        draft.metadata.interactionState.selectedNodeIds.delete(change.id);
        delete draft.metadata.interactionState.nodePositionsByNodeId[change.id];

        // Post-processing
        cleanupOrphanedEdgesAfterNodeRemoval(draft, change.id);
        updateRouterConfigsAfterEdgeChange(draft, FlowchartEdgeChangeType.REMOVE);
        break;
      case FlowchartNodeChangeType.REPLACE: {
        const nodeToReplace = draft.staticFlowchart.nodesStaticData.find((node) => node.id === change.id);
        if (nodeToReplace) {
          const newNodeData: StaticFlowchartNodeData = extractStaticData(change.data);
          Object.assign(nodeToReplace, newNodeData);
        }
        break;
      }
      case FlowchartNodeChangeType.DIMENSIONS: {
        const dimensions = change.dimensions;
        if (dimensions != null) {
          setNodeDimensions((prevDimensions) => ({
            ...prevDimensions,
            [change.id]: dimensions,
          }));
        }
        break;
      }
      case FlowchartNodeChangeType.RESET:
        // TODO: Explore if reset changes need to be handled?
        break;
      default:
        assert(false, 'Unexpected node change type', {change});
    }
  });
}

function applyFlowchartEdgeChanges(
  edgeChanges: Array<FlowchartEdgeChange>,
  draft: Draft<FlowchartConfigWithMetadata>
): void {
  // Pre-processing
  const allEdgeChanges = processEdgeChangesWithMirroring(edgeChanges, draft);
  const validatedEdgeChanges = validateEdgeChanges(allEdgeChanges, draft.staticFlowchart.edgesStaticData);

  validatedEdgeChanges.forEach((change) => {
    switch (change.type) {
      case FlowchartEdgeChangeType.ADD:
        if (change.item != null) {
          assert(change.item.sourceHandle != null, 'Source handle is required');
          assert(change.item.targetHandle != null, 'Target handle is required');
          assert(change.item.type != null, 'Type is required');
          const newEdge: FlowchartStaticEdgeData = {
            id: change.item.id,
            source: change.item.source,
            target: change.item.target,
            sourceHandle: change.item.sourceHandle,
            targetHandle: change.item.targetHandle,
            type: change.item.type as ProcessFlowchartEdgeType,
          };
          draft.staticFlowchart.edgesStaticData.push(newEdge);

          // Post-processing
          updateRouterConfigsAfterEdgeChange(draft, FlowchartEdgeChangeType.ADD);
        }
        break;
      case FlowchartEdgeChangeType.SELECT:
        if (change.selected) {
          draft.metadata.interactionState.selectedEdgeIds.add(change.id);
        } else {
          draft.metadata.interactionState.selectedEdgeIds.delete(change.id);
        }
        break;
      case FlowchartEdgeChangeType.REMOVE:
        draft.staticFlowchart.edgesStaticData = draft.staticFlowchart.edgesStaticData.filter(
          (edge) => edge.id !== change.id
        );
        draft.metadata.interactionState.selectedEdgeIds.delete(change.id);

        // Clean up custom mapping data for removed edge
        if (draft.metadata.customMappingDataByEdgeId[change.id] != null) {
          delete draft.metadata.customMappingDataByEdgeId[change.id];
        }

        // Post-processing
        updateRouterConfigsAfterEdgeChange(draft, FlowchartEdgeChangeType.REMOVE, change.id);
        break;
      case FlowchartEdgeChangeType.LABEL:
        draft.metadata.interactionState.edgeLabelsByEdgeId[change.id] = change.label;
        break;
      case FlowchartEdgeChangeType.RESET:
        // TODO: Explore if reset changes need to be handled?
        break;
      default:
        assert(false, 'Unexpected edge change type', {change});
    }
  });
}

function applyTransitionSourceChanges(
  transitionSourceChanges: Array<FlowchartTransitionSourceChange>,
  draft: Draft<FlowchartConfigWithMetadata>
): void {
  transitionSourceChanges.forEach((change) => {
    switch (change.type) {
      case FlowchartTransitionSourceChangeType.ADD:
        draft.metadata.transitionSourceByNodeIdPair[change.id] = change.transitionSource;
        break;
      case FlowchartTransitionSourceChangeType.REMOVE:
        delete draft.metadata.transitionSourceByNodeIdPair[change.id];
        break;
      case FlowchartTransitionSourceChangeType.REPLACE:
        draft.metadata.transitionSourceByNodeIdPair[change.id] = change.transitionSource;
        break;
      default:
        assert(false, 'Unexpected transition source change type', {change});
    }
  });
}

function applyCustomMappingChanges(
  customMappingChanges: Array<FlowchartCustomMappingDataChange>,
  draft: Draft<FlowchartConfigWithMetadata>
): void {
  customMappingChanges.forEach((change) => {
    switch (change.type) {
      case FlowchartCustomMappingDataChangeType.ADD:
        draft.metadata.customMappingDataByEdgeId[change.id] = change.customMapping;
        break;
      case FlowchartCustomMappingDataChangeType.REMOVE:
        delete draft.metadata.customMappingDataByEdgeId[change.id];
        break;
      case FlowchartCustomMappingDataChangeType.REPLACE:
        draft.metadata.customMappingDataByEdgeId[change.id] = change.customMapping;
        break;
      default:
        assert(false, 'Unexpected custom mapping data change type', {change});
        break;
    }
  });
}

/**
 * Detects which parts of the flowchart state have changed and returns only the changed parts.
 *
 * Uses Immer's structural sharing: unchanged parts keep their original references, while changed parts get new references.
 * This enables fast reference equality checks to identify what actually changed.
 *
 * We return only changed parts (not the entire state) so reduceFlowchartConfig can merge these changes with the latest server state.
 */
function detectChangedParts(
  original: FlowchartConfigWithMetadata,
  updated: FlowchartConfigWithMetadata
): FlowchartConfigWithPartialMetadata | null {
  // Early short-circuit: if both references are the same, nothing changed
  // (This only happens if produce() was never called)
  if (original === updated) {
    return null;
  }

  const staticFlowchartChanged = updated.staticFlowchart !== original.staticFlowchart;

  const interactionStateChanged = updated.metadata.interactionState !== original.metadata.interactionState;
  const transitionSourceChanged =
    updated.metadata.transitionSourceByNodeIdPair !== original.metadata.transitionSourceByNodeIdPair;
  const customMappingChanged =
    updated.metadata.customMappingDataByEdgeId !== original.metadata.customMappingDataByEdgeId;

  const anyMetadataChanged = interactionStateChanged || transitionSourceChanged || customMappingChanged;

  // Early short-circuit: if nothing actually changed, return null
  if (!staticFlowchartChanged && !anyMetadataChanged) {
    return null;
  }

  // Build the result object based on what actually changed
  const buildMetadataChanges = (): Partial<FlowchartConfigMetadata> => {
    const metadataChanges: Partial<FlowchartConfigMetadata> = {};

    if (interactionStateChanged) {
      metadataChanges.interactionState = updated.metadata.interactionState;
    }

    if (transitionSourceChanged) {
      metadataChanges.transitionSourceByNodeIdPair = updated.metadata.transitionSourceByNodeIdPair;
    }

    if (customMappingChanged) {
      metadataChanges.customMappingDataByEdgeId = updated.metadata.customMappingDataByEdgeId;
    }

    return metadataChanges;
  };

  if (staticFlowchartChanged && anyMetadataChanged) {
    return {
      staticFlowchart: updated.staticFlowchart,
      flowchartConfigMetadata: buildMetadataChanges(),
    };
  } else if (staticFlowchartChanged) {
    return {
      staticFlowchart: updated.staticFlowchart,
    };
  } else {
    return {
      flowchartConfigMetadata: buildMetadataChanges(),
    };
  }
}

/**
 * Extracts the static data from the data object of a flowchart node.
 * @param nodeData The data object on a FlowchartNode.
 * @returns The static data of the node.
 */
export function extractStaticData(nodeData: FlowchartNode['data']): StaticFlowchartNodeData {
  return omit(
    nodeData,
    ...(INTERACTION_FIELDS as unknown as Array<keyof FlowchartNode['data']>)
  ) as StaticFlowchartNodeData;
}

/**
 * Core logic for applying flowchart changes to static data, metadata, and interactive state.
 */
export function applyFlowchartChangesCore(
  currentState: FlowchartConfigWithMetadata,
  changes: {
    nodeChanges?: Array<FlowchartNodeChange>;
    edgeChanges?: Array<FlowchartEdgeChange>;
    transitionSourceChanges?: Array<FlowchartTransitionSourceChange>;
    customMappingChanges?: Array<FlowchartCustomMappingDataChange>;
  },
  setNodeDimensions: React.Dispatch<React.SetStateAction<Record<string, {width: number; height: number}>>>
): FlowchartConfigWithPartialMetadata | null {
  const {
    nodeChanges = [],
    edgeChanges = [],
    transitionSourceChanges = [],
    customMappingChanges = [],
  } = changes;

  if (
    nodeChanges.length === 0 &&
    edgeChanges.length === 0 &&
    transitionSourceChanges.length === 0 &&
    customMappingChanges.length === 0
  ) {
    return null;
  }

  const updatedFlowchartAndMetadata = produce(currentState, (draft) => {
    applyFlowchartNodeChanges(nodeChanges, draft, setNodeDimensions);
    applyFlowchartEdgeChanges(edgeChanges, draft);
    applyTransitionSourceChanges(transitionSourceChanges, draft);
    applyCustomMappingChanges(customMappingChanges, draft);
  });

  return detectChangedParts(currentState, updatedFlowchartAndMetadata);
}

/**
 * @deprecated TODO(BNCH-135245): To be removed, will switch to using applyFlowchartChanges instead.
 * Applies changes to a flowchart and returns the updated nodes and edges.
 *
 * @param nodeChanges - The changes to the nodes in the flowchart.
 * @param edgeChanges - The changes to the edges in the flowchart.
 * @param nodes - The nodes in the flowchart.
 * @param edges - The edges in the flowchart.
 * @returns An object containing the new nodes and edges as well as booleans describing whether or not these
 *   changes constitute an update to the static data of the flowchart or the structure of the flowchart.
 */
export function processInteractiveFlowchartChanges({
  nodeChanges,
  edgeChanges,
  nodes,
  edges,
}: {
  nodeChanges: Array<FlowchartNodeChange>;
  edgeChanges: Array<FlowchartEdgeChange>;
  nodes: Array<FlowchartNode>;
  edges: Array<FlowchartEdge>;
}): FlowchartChangeResult {
  const reactFlowNodeChanges = nodeChanges.filter(
    (nodeChange): nodeChange is NodeChange => nodeChange.type !== 'replace'
  );
  const nodesWithReactFlowChanges = applyNodeChanges(reactFlowNodeChanges, nodes);
  let updatedNodes = produce(nodesWithReactFlowChanges, (draft) => {
    for (const nodeChange of nodeChanges) {
      if (nodeChange.type === 'replace') {
        const updatedNode = draft.find((node) => node.id === nodeChange.id);
        if (updatedNode != null) {
          updatedNode.data = nodeChange.data;
        }
      }
    }
  });
  const validEdgeChanges = edgeChanges.filter(
    (edgeChange) => edgeChange.type !== 'add' || isNewEdgeValidForFlowchart(edgeChange.item, edges)
  );
  const reactFlowEdgeChanges = validEdgeChanges.filter(
    (edgeChange): edgeChange is EdgeChange => edgeChange.type !== 'label'
  );
  const reactFlowEdgesWithChanges = applyEdgeChanges(reactFlowEdgeChanges, edges) as Array<FlowchartEdge>;
  const updatedEdges = produce(reactFlowEdgesWithChanges, (draft) => {
    for (const edgeChange of edgeChanges) {
      if (edgeChange.type === 'label') {
        const updatedEdge = draft.find((edge) => edge.id === edgeChange.id);
        if (updatedEdge != null) {
          updatedEdge.label = edgeChange.label;
        }
      }
    }
  }).filter((edge) => {
    // Remove edges for nodes that were removed from the flowchart
    const sourceNode = updatedNodes.find((node) => node.id === edge.source);
    const targetNode = updatedNodes.find((node) => node.id === edge.target);
    return sourceNode != null && targetNode != null;
  });

  updatedNodes = produce(updatedNodes, (draft) => {
    const edgeClientIdsRemoved = compact(
      validEdgeChanges
        .filter((edgeChange) => edgeChange.type === FlowchartEdgeChangeType.REMOVE)
        .map((edgeChange) => edgeChange.id)
    );

    for (const node of draft) {
      if (!isInteractiveRouterNode(node)) {
        continue;
      }

      let routerConfig: RouterConfig = {...node.data.routerConfig};
      // Remove function specs for edges that were removed
      for (const edgeClientId of edgeClientIdsRemoved) {
        routerConfig = removeFunctionSpecsUsingEdge(edgeClientId, routerConfig);
      }

      // Handle adding function specs for new edges and removing unused function specs
      const numOutboundEdges = updatedEdges.filter((edge) => edge.source === node.id).length;
      const numEdgesWithoutFunctionSpecs = getEdgesRouterFunctionsDifference(routerConfig, numOutboundEdges);
      if (numEdgesWithoutFunctionSpecs > 0) {
        routerConfig = addUnconfiguredFunctionsForRouterConfig(
          routerConfig,
          numEdgesWithoutFunctionSpecs,
          numOutboundEdges
        );
      } else if (numEdgesWithoutFunctionSpecs < 0) {
        routerConfig = removeUnusedFunctionSpecs(routerConfig, Math.abs(numEdgesWithoutFunctionSpecs));
      }
      node.data.routerConfig = routerConfig;
    }
  });

  // Only update static flowchart data if the static data has actually changed
  const hasNodeDataChanges =
    updatedNodes.length !== nodes.length ||
    updatedNodes.some((updatedNode) => {
      const originalNode = nodes.find((node) => node.id === updatedNode.id);
      if (!originalNode) {
        return true; // New node
      }

      const originalStaticData = extractStaticData(originalNode.data);
      const updatedStaticData = extractStaticData(updatedNode.data);
      return !areEqual(originalStaticData, updatedStaticData);
    });

  // Only update static flowchart data if edges were added or removed
  const currentEdgeIds = new Set(edges.map((edge) => edge.id));
  const updatedEdgeIds = new Set(updatedEdges.map((edge) => edge.id));

  // Check if any edges were added or removed
  const hasStructuralChanges =
    currentEdgeIds.size !== updatedEdgeIds.size ||
    [...currentEdgeIds].some((id) => !updatedEdgeIds.has(id)) ||
    [...updatedEdgeIds].some((id) => !currentEdgeIds.has(id));

  return {
    updatedNodes,
    updatedEdges,
    hasNodeDataChanges,
    hasStructuralChanges,
  };
}

/**
 * Contains the core flowchart state management logic for the horizontal flowchart.
 *
 * Main features:
 * - Apply changes to the flowchart and/or metadata
 * - Handle right sidepanel state management
 * - Handle reactflow actions related to nodes + edges
 *
 * Core concepts:
 * - Minimal flowchart representations to actively track:
 *      We should rarely, if ever, need to mutate or interact with the interactive nodes/edges directly,
 *      which gets sent to reactflow for rendering. Instead, we change the static flowchart, metadata and interactive
 *      state trackers as needed, and the interactive flowchart is rebuilt from those sources of truth automatically.
 * - All change management is funneled through the applyFlowchartChanges function:
 *      This allows for centralizing side effects and autohealing logic related to flowchart changes.
 *

 * @returns The flowchart state management logic.
 */
const useFlowchartState = (params: UseFlowchartStateParams): UseFlowchartStateReturn => {
  const {staticFlowchart, flowchartConfigMetadata, validationErrors, setFlowchartAndMetadata, addNodeParams} =
    params;

  const locked = LockedContext.useContext();

  const [interactiveNodes, setInteractiveNodes] = useState<Array<FlowchartNode>>([]);
  const [interactiveEdges, setInteractiveEdges] = useState<Array<FlowchartEdge>>([]);
  const [nodeDimensions, setNodeDimensions] = useState<Record<string, {width: number; height: number}>>({});

  const [isRightSidepanelOpen, setIsRightSidepanelOpen] = useState(false);
  const [selectedNodeIdForRightSidepanel, setSelectedNodeIdForRightSidepanel] = useState<string | null>(null);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);

  const [_sourceNodeAndNewNodeBeingAdded, setSourceNodeAndNewNodeBeingAdded] = useState<
    [string, FlowchartNode] | null
  >(null);

  const [placeholderNodeIdBeingAdded, setPlaceholderNodeBeingAdded] = useState<string | null>(null);

  const applyFlowchartChanges = useCallback(
    (changeGenerator: FlowchartChangeGenerator, saveImmediately: boolean = false) => {
      setFlowchartAndMetadata((currentState) => {
        if (!currentState) {
          return null;
        }

        const changes = changeGenerator(currentState);

        // When locked, allow selection changes so the sidepanel can open in read-only mode,
        // but block all other changes (add/remove/replace nodes, edge changes, metadata changes)
        if (locked) {
          const onlySelection =
            (changes.nodeChanges ?? []).every((c) => c.type === FlowchartNodeChangeType.SELECT) &&
            (changes.edgeChanges ?? []).every((c) => c.type === FlowchartEdgeChangeType.SELECT) &&
            !changes.transitionSourceChanges?.length &&
            !changes.customMappingChanges?.length;
          if (!onlySelection) {
            return null;
          }
        }

        return applyFlowchartChangesCore(currentState, changes, setNodeDimensions);
      }, saveImmediately);
    },
    [setFlowchartAndMetadata, locked]
  );

  const openSidepanelAndSelectNode = useCallback(
    (nodeId: string, fieldId: string | null = null): void => {
      setIsRightSidepanelOpen(true);
      setSelectedNodeIdForRightSidepanel(nodeId);
      setSelectedFieldId(fieldId);

      applyFlowchartChanges((flowchartConfigWithMetadata) => {
        const nodeChanges: Array<FlowchartNodeChange> = [];

        // Deselect any currently selected nodes
        const currentlySelectedNodeIds =
          flowchartConfigWithMetadata.metadata.interactionState.selectedNodeIds;
        currentlySelectedNodeIds.forEach((selectedId: string) => {
          if (selectedId !== nodeId) {
            nodeChanges.push({
              type: FlowchartNodeChangeType.SELECT,
              id: selectedId,
              selected: false,
            });
          }
        });

        // Select the target node
        nodeChanges.push({
          type: FlowchartNodeChangeType.SELECT,
          id: nodeId,
          selected: true,
        });

        return {nodeChanges};
      });
    },
    [applyFlowchartChanges]
  );

  const openSidepanelForNode = useCallback(
    (nodeId: string): void => {
      openSidepanelAndSelectNode(nodeId, null);
    },
    [openSidepanelAndSelectNode]
  );

  const openSidepanelForNodeWithField = useCallback(
    (nodeId: string, fieldId: string): void => {
      openSidepanelAndSelectNode(nodeId, fieldId);
    },
    [openSidepanelAndSelectNode]
  );

  const getAddNodeOptionsForRightHandle = useCallback(
    (sourceNodeId: string, reactFlowInstance: ReactFlowInstance): Array<AddNodeOption> | null =>
      getAddNodeOptions({
        flowchartEditorConfigurator: addNodeParams.flowchartEditorConfigurator,
        sourceNodeId,
        reactFlowInstance,
        singletonTaskSchemasByName: addNodeParams.singletonTaskSchemasByName,
        canAddExistingTaskSchema: addNodeParams.taskDefinitionsForSidepanel.length > 0,
        rootTaskSchema: addNodeParams.rootTaskSchema,
        openSidepanelForNode,
        openSidepanelForNodeWithField,
        onNodeChangesOnly: (nodeChangeGenerator) => {
          applyFlowchartChanges((flowchartConfig) => ({
            nodeChanges: nodeChangeGenerator(flowchartConfig),
            edgeChanges: [],
          }));
        },
        setSourceNodeAndNewNodeBeingAdded,
        didInitialFlowchartHaveChainedRouters: addNodeParams.didInitialFlowchartHaveChainedRouters,
      }),
    [addNodeParams, openSidepanelForNode, openSidepanelForNodeWithField, applyFlowchartChanges]
  );

  const interactiveFlowchartOptions: FlowchartOptions = useMemo(
    () => ({
      onNodeHeaderClick: openSidepanelForNode,
      onFieldClick: openSidepanelForNodeWithField,
      onUpdateNodeData: (nodeChangeGenerator) => {
        applyFlowchartChanges((flowchartConfig) => ({
          nodeChanges: nodeChangeGenerator(flowchartConfig),
          edgeChanges: [],
        }));
      },
      getAddNodeOptionsForRightHandle,
      validationErrors,
    }),
    [
      openSidepanelForNode,
      openSidepanelForNodeWithField,
      getAddNodeOptionsForRightHandle,
      validationErrors,
      applyFlowchartChanges,
    ]
  );

  useEffect(() => {
    if (!staticFlowchart) {
      return;
    }

    const {nodes, edges} = buildInteractiveFlowchartFromStaticData({
      staticNodesData: staticFlowchart.nodesStaticData,
      staticEdgesData: staticFlowchart.edgesStaticData,
      options: interactiveFlowchartOptions,
      nodePositionsByNodeId: flowchartConfigMetadata.interactionState.nodePositionsByNodeId,
      selectedNodeIds: flowchartConfigMetadata.interactionState.selectedNodeIds,
      selectedEdgeIds: flowchartConfigMetadata.interactionState.selectedEdgeIds,
      validationErrors,
      nodeDimensions,
    });
    setInteractiveNodes(nodes);
    setInteractiveEdges(edges);
  }, [
    staticFlowchart,
    flowchartConfigMetadata.interactionState.nodePositionsByNodeId,
    flowchartConfigMetadata.interactionState.selectedNodeIds,
    flowchartConfigMetadata.interactionState.selectedEdgeIds,
    interactiveFlowchartOptions,
    validationErrors,
    nodeDimensions,
  ]);

  // Handle adding nodes from right handle
  useEffect(() => {
    if (_sourceNodeAndNewNodeBeingAdded != null) {
      const [sourceNodeId, newNode] = _sourceNodeAndNewNodeBeingAdded;
      applyFlowchartChanges((flowchartDraftState) => ({
        nodeChanges: [
          {
            type: 'add',
            item: {
              ...newNode,
              data: {
                ...newNode.data,
                getAddNodeOptionsForRightHandle,
              },
            },
          },
        ],
        edgeChanges: [
          {
            type: 'add',
            item: buildControlFlowEdge({
              sourceNodeId,
              targetNodeId: newNode.id,
            }),
          },
        ],
      }));

      // If it's a placeholder node, set it up for sidepanel opening
      if (isInteractivePlaceholderNode(newNode)) {
        setPlaceholderNodeBeingAdded(newNode.id);
      }

      setSourceNodeAndNewNodeBeingAdded(null);
    }
  }, [
    _sourceNodeAndNewNodeBeingAdded,
    setSourceNodeAndNewNodeBeingAdded,
    applyFlowchartChanges,
    getAddNodeOptionsForRightHandle,
  ]);

  // Handle opening sidepanel for placeholder nodes
  useEffect(() => {
    if (placeholderNodeIdBeingAdded != null) {
      // Select the placeholder node
      applyFlowchartChanges((flowchartDraftState) => ({
        nodeChanges: [
          {
            type: 'select',
            id: placeholderNodeIdBeingAdded,
            selected: true,
          },
        ],
      }));
      // Open the sidepanel
      openSidepanelForNode(placeholderNodeIdBeingAdded);
      setPlaceholderNodeBeingAdded(null);
    }
  }, [
    placeholderNodeIdBeingAdded,
    openSidepanelForNode,
    setPlaceholderNodeBeingAdded,
    applyFlowchartChanges,
  ]);

  // Should only be used for reactflow-specific node changes
  // When the FlowchartConfigWithMetadata can't be used to generate changes
  const onReactFlowNodeChangesOnly = useCallback(
    (nodeChanges: Array<FlowchartNodeChange>) => {
      applyFlowchartChanges((flowchartConfig) => ({nodeChanges, edgeChanges: []}));
    },
    [applyFlowchartChanges]
  );

  // Should only be used for reactflow-specific edge changes
  // When the FlowchartConfigWithMetadata can't be used to generate changes
  const onReactFlowEdgeChangesOnly = useCallback(
    (edgeChanges: Array<FlowchartEdgeChange>) => {
      applyFlowchartChanges((flowchartConfig) => ({nodeChanges: [], edgeChanges}));
    },
    [applyFlowchartChanges]
  );

  const handleRightSidepanelClose = useCallback(() => {
    const nodeIdToDeselect = selectedNodeIdForRightSidepanel;
    setIsRightSidepanelOpen(false);
    setSelectedNodeIdForRightSidepanel(null);
    setSelectedFieldId(null);
    if (nodeIdToDeselect) {
      applyFlowchartChanges((flowchartConfig) => ({
        nodeChanges: [
          {
            type: FlowchartNodeChangeType.SELECT,
            id: nodeIdToDeselect,
            selected: false,
          },
        ],
      }));
    }
  }, [applyFlowchartChanges, selectedNodeIdForRightSidepanel]);

  const onConnect = useCallback(
    (connection: Connection): void => {
      if (
        connection.source != null &&
        connection.target != null &&
        connection.sourceHandle != null &&
        connection.targetHandle != null
      ) {
        const sourceHandleType = getEdgeTypeFromHandleId(connection.sourceHandle);
        const targetHandleType = getEdgeTypeFromHandleId(connection.targetHandle);

        const edgeType =
          sourceHandleType === targetHandleType ? sourceHandleType : ProcessFlowchartEdgeType.CONTROL_FLOW;

        applyFlowchartChanges((flowchartConfig) => ({
          edgeChanges: [
            {
              type: FlowchartEdgeChangeType.ADD,
              item: {
                id: uuidv4(),
                source: connection.source!,
                target: connection.target!,
                sourceHandle: connection.sourceHandle,
                targetHandle: connection.targetHandle,
                type: edgeType,
              },
            },
          ],
        }));
      }
    },
    [applyFlowchartChanges]
  );

  const setNodesViaChangesForLayout = useCallback(
    (newNodes: Array<Node>): void => {
      const nodeChanges: Array<FlowchartNodeChange> = newNodes.map((node) => ({
        type: FlowchartNodeChangeType.POSITION,
        id: node.id,
        position: node.position,
      }));
      onReactFlowNodeChangesOnly(nodeChanges);
    },
    [onReactFlowNodeChangesOnly]
  );

  const setEdgesViaChangesForLayout = useCallback((newEdges: Array<Edge>): void => {
    // For now, edges don't have position changes, but we could handle other edge updates here
    // This maintains the interface expected by ControlledAutoLayoutButton
  }, []);

  return {
    // State
    interactiveNodes,
    interactiveEdges,
    // Core functions
    applyFlowchartChanges,
    onReactFlowNodeChangesOnly,
    onReactFlowEdgeChangesOnly,
    onConnect,
    setNodesViaChangesForLayout,
    setEdgesViaChangesForLayout,
    // Sidepanel state
    isRightSidepanelOpen,
    selectedFieldId,
    selectedNodeIdForRightSidepanel,
    setSelectedFieldId,
    // Sidepanel functions
    handleRightSidepanelClose,
    openSidepanelForNode,
    openSidepanelForNodeWithField,
    getAddNodeOptionsForRightHandle,
    // Node addition state
    sourceNodeAndNewNodeBeingAdded: _sourceNodeAndNewNodeBeingAdded,
    setSourceNodeAndNewNodeBeingAdded,
    placeholderNodeIdBeingAdded,
    setPlaceholderNodeBeingAdded,
  };
};

export default useFlowchartState;
